import type { ReactNode } from 'react';

export type CommandSelectOptionId = string | number;

export interface CommandSelectOption {
    id: CommandSelectOptionId;
    label: string;
    icon?: ReactNode;
    shortcutHint?: string;
    disabled?: boolean;
}

export interface CommandSelectGroupTitle {
    type: 'group-title';
    label: string;
}

export type CommandSelectItem = CommandSelectOption | CommandSelectGroupTitle;

export function isCommandSelectOption(item: CommandSelectItem): item is CommandSelectOption {
    return 'id' in item && 'label' in item;
}

export function isCommandSelectGroupTitle(item: CommandSelectItem): item is CommandSelectGroupTitle {
    return 'type' in item && item.type === 'group-title';
}

export interface CommandSelectProps {
    id?: string;
    items: CommandSelectItem[];
    value?: CommandSelectOptionId | null;
    defaultValue?: CommandSelectOptionId | null;
    onChange?: (value: CommandSelectOptionId | null, option: CommandSelectOption | null) => void;
    /**
     * `badge` (default): compact trigger, `label` is shown inside it until a value is selected.
     * `field`: form field look, aligned with the other inputs — `label` above, `placeholder` inside.
     */
    variant?: 'badge' | 'field';
    label?: string;
    placeholder?: string;
    required?: boolean;
    description?: string;
    icon?: ReactNode;
    badgeVariant?: 'solid' | 'soft' | 'outline';
    size?: 'sm' | 'md' | 'lg';
    className?: string;
    fullWidth?: boolean;
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    disabled?: boolean;
    treatIndex0AsNull?: boolean;
    searchable?: boolean;
    searchPlaceholder?: string;
    autoFocusSearchOnOpen?: boolean;
    focusSearchOnKey?: 's' | null;
    onSearchChange?: (q: string) => void;
    filterOptions?: (q: string, items: CommandSelectItem[]) => CommandSelectItem[];
    loadOptions?: (q: string) => Promise<CommandSelectItem[]>;
    loadingText?: string;
    emptyText?: string;
    onCreate?: (label: string) => Promise<CommandSelectOption | null> | CommandSelectOption | null;
    createActionLabel?: string | ((q: string) => string);
    showCreateWhenNoMatch?: boolean;
    enableNumericShortcuts?: boolean;
    numericShortcutOffset?: number;
    ariaLabel?: string;
    ariaLabelledBy?: string;
    portal?: boolean;
    placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
    maxHeight?: number;
    resetSearchOnClose?: boolean;
    errorMessage?: string;
    dataTestid?: string;
}
